import { apiClient } from "@/lib/api";
import type {
  StandardResponse,
  PaginatedStandardResponse,
  MessageResponse,
  ErrorResponse,
  PaginationInfo,
} from "@/types/api-response";
import {
  isErrorResponse,
  extractData,
  extractPaginatedData,
} from "@/types/api-response";
import type { ResourcePowerAction } from "@/lib/resourcePowerActions";
import type {
  Resource,
  ResourceDetail,
  ResourceFilters,
  ResourceAction,
  ResourceMetricsSeries,
  ResourceMetricsResponse,
  MetricDataPoint,
  CostAnalysis,
  ResourceSnapshot,
  ResourceAttachmentsResponse,
  ResourceActivityLogEntry,
  N8nEndpoint,
  N8nProbeResult,
  ResourcePublicIpUpdate,
  ApplyVmIpAddressPayload,
  ApplyVmIpAddressResult,
  ResourceProductLineAuditRow,
  ResourceProductLineQuote,
  ResourceProductLineUpdate,
  ResourceConfigChangeQuote,
  ResourceConfigChangeLimits,
  ResourceConfigChangeExecution,
  ResourceConfigChangeTarget,
  UpdateByUuidPreview,
  UpdateByUuidExecution,
  ResetVmPasswordResult,
  ResourceConsole,
  TransferResourceOwnershipPayload,
  TransferResourceOwnershipResult,
  N8nPowerAction,
  N8nVmActionResult,
  FirewallRulesResponse,
  FirewallLog,
  FirewallMacro,
  FirewallOptions,
  CreateFirewallRuleRequest,
  BulkStopResourcesPayload,
  BulkStopResourcesResult,
  ExpiredVm,
  ResourceProviderState,
  ExpiredVmFilters,
  DeleteExpiredVmsPayload,
  DeleteExpiredVmsResult,
  ExpiredVmCounts,
  OrphanChild,
  OrphanChildrenFilters,
  RemoveOrphanChildrenPayload,
  RemoveOrphanChildrenResult,
  ResourceBackfillField,
  ResourceFieldBackfillResult,
  DeleteResourcePayload,
  DeleteResourceRecordPayload,
  ReinstallResourcePayload,
  RetryProvisioningResult,
  ProvisioningRetryCheck,
  ResourceStatus,
  ResourceStatusSnapshot,
  ResourceStatusUpdate,
  ResourceActionListItem,
  ResourceActionListFilters,
  ResourceReinstallListItem,
  ResourceReinstallListFilters,
  ResourceReinstallReport,
  ResourceReinstallReportFilters,
} from "@/types/admin";

export interface MetricsQueryParams {
  from: string;
  to: string;
  interval?: "1m" | "5m" | "15m" | "1h" | "1d";
}

export const adminResourceService = {
  /**
   * Get paginated list of resources with filters
   * Returns: { data: Resource[]; pagination: PaginationInfo }
   */
  async getResources(
    filters: ResourceFilters,
  ): Promise<{ data: Resource[]; pagination: PaginationInfo }> {
    // Names must match GetResourcesQuery's properties — the endpoint model-binds this query
    // string straight onto it, so `page`/`limit` were silently ignored and every request came
    // back as page 1. `type`/`status` bind to the backend enums, so only enum names are accepted.
    // The free-text term binds to GetResourcesQuery.Search: `keywords` matches no property on
    // that query, so the whole search box was dropped server-side and every term returned the
    // unfiltered list.
    const params = new URLSearchParams();

    if (filters.search) params.append("search", filters.search);
    if (filters.type && filters.type !== "All")
      params.append("type", filters.type);
    if (filters.status === "All") {
      params.append("allStatuses", "true");
    } else if (filters.status && filters.status !== "Active") {
      params.append("status", filters.status);
    }
    if (filters.tenantId) params.append("tenantId", filters.tenantId);
    if (filters.createdFrom) params.append("createdFrom", filters.createdFrom);
    if (filters.createdTo) params.append("createdTo", filters.createdTo);
    if (filters.expiringInDays)
      params.append("expiringInDays", filters.expiringInDays.toString());
    if (filters.productLineSlug)
      params.append("productLineSlug", filters.productLineSlug);
    params.append("pageNumber", filters.page.toString());
    params.append("pageSize", filters.pageSize.toString());

    const response = await apiClient.get<
      PaginatedStandardResponse<Resource> | ErrorResponse
    >(`/api/v1/admin/resources?${params.toString()}`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractPaginatedData(response.data);
  },

  /**
   * Get resource details by ID
   * Returns: ResourceDetail (extracted from StandardResponse<ResourceDetail>)
   */
  async getResourceById(id: string): Promise<ResourceDetail> {
    const response = await apiClient.get<
      StandardResponse<ResourceDetail> | ErrorResponse
    >(`/api/v1/admin/resources/${id}`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * The current state of each resource in `ids`, keyed by id, across tenants and including deleted
   * rows; unknown ids are absent. The backend accepts at most 100 ids, sent as repeated `ids`
   * params (`?ids=a&ids=b`), which is how ASP.NET binds a `List<Guid>`.
   */
  async getStatuses(
    ids: string[],
  ): Promise<Record<string, ResourceStatusSnapshot>> {
    const params = new URLSearchParams();
    ids.forEach((id) => params.append("ids", id));

    const response = await apiClient.get<
      StandardResponse<Record<string, ResourceStatusSnapshot>> | ErrorResponse
    >(`/api/v1/admin/resources/statuses?${params.toString()}`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Get resource action history
   * Returns: ResourceAction[] (extracted from StandardResponse<ResourceAction[]>)
   */
  async getResourceActions(id: string): Promise<ResourceAction[]> {
    const response = await apiClient.get<
      StandardResponse<ResourceAction[]> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/actions`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Get one page of the cross-resource action trail (SuperAdmin, SupportAdmin).
   *
   * Same shape as `getResources`: names must match the query the endpoint model-binds onto —
   * `pageNumber`/`pageSize`, not `page`/`limit` — and `createdFrom`/`createdTo` are the same
   * whole-local-day range every other admin list sends via `formatDateRangeForAPI`.
   * Returns: { data: ResourceActionListItem[]; pagination: PaginationInfo }
   */
  async getResourceActionList(
    filters: ResourceActionListFilters,
  ): Promise<{ data: ResourceActionListItem[]; pagination: PaginationInfo }> {
    const params = new URLSearchParams();

    if (filters.search) params.append("search", filters.search);
    if (filters.tenantId) params.append("tenantId", filters.tenantId);
    if (filters.resourceId) params.append("resourceId", filters.resourceId);
    if (filters.createdFrom) params.append("createdFrom", filters.createdFrom);
    if (filters.createdTo) params.append("createdTo", filters.createdTo);
    params.append("pageNumber", filters.page.toString());
    params.append("pageSize", filters.pageSize.toString());

    const response = await apiClient.get<
      PaginatedStandardResponse<ResourceActionListItem> | ErrorResponse
    >(`/api/v1/admin/resource-actions?${params.toString()}`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractPaginatedData(response.data);
  },

  /**
   * Get one page of the VM-reinstall report (SuperAdmin, SupportAdmin), newest request first.
   * Returns: { data: ResourceReinstallListItem[]; pagination: PaginationInfo }
   */
  async getResourceReinstalls(filters: ResourceReinstallListFilters): Promise<{
    data: ResourceReinstallListItem[];
    pagination: PaginationInfo;
  }> {
    const params = new URLSearchParams();

    if (filters.search) params.append("search", filters.search);
    if (filters.vmPlan) params.append("vmPlan", filters.vmPlan);
    if (filters.requestedFrom)
      params.append("requestedFrom", filters.requestedFrom);
    if (filters.requestedTo) params.append("requestedTo", filters.requestedTo);
    params.append("pageNumber", filters.page.toString());
    params.append("pageSize", filters.pageSize.toString());

    const response = await apiClient.get<
      PaginatedStandardResponse<ResourceReinstallListItem> | ErrorResponse
    >(`/api/v1/admin/resource-reinstalls?${params.toString()}`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractPaginatedData(response.data);
  },

  /**
   * Get the VM-reinstall report for a requested-at window (SuperAdmin, SupportAdmin): totals,
   * reinstalls per day, and the VMs and tenants that reinstalled most.
   * Returns: ResourceReinstallReport
   */
  async getResourceReinstallReport(
    filters: ResourceReinstallReportFilters,
  ): Promise<ResourceReinstallReport> {
    const params = new URLSearchParams();

    params.append("requestedFrom", filters.requestedFrom);
    params.append("requestedTo", filters.requestedTo);
    if (filters.search) params.append("search", filters.search);
    if (filters.vmPlan) params.append("vmPlan", filters.vmPlan);

    const response = await apiClient.get<
      StandardResponse<ResourceReinstallReport> | ErrorResponse
    >(`/api/v1/admin/resource-reinstalls/report?${params.toString()}`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Remove one row of the VM-reinstall report (SuperAdmin only). The row is no longer counted
   * against the customer's reinstall limit.
   */
  async deleteResourceReinstall(id: string): Promise<void> {
    const response = await apiClient.delete<MessageResponse | ErrorResponse>(
      `/api/v1/admin/resource-reinstalls/${id}`,
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }
  },

  /**
   * Get resource metrics for a time range
   * Returns: ResourceMetricsSeries (extracted from StandardResponse<ResourceMetricsSeries>)
   */
  async getResourceMetrics(
    id: string,
    params: MetricsQueryParams,
  ): Promise<ResourceMetricsSeries> {
    const queryParams = new URLSearchParams();
    queryParams.append("from", params.from);
    queryParams.append("to", params.to);
    if (params.interval) queryParams.append("interval", params.interval);

    const response = await apiClient.get<
      StandardResponse<ResourceMetricsResponse> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/metrics?${queryParams.toString()}`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    const data = extractData(response.data);

    // Same transform as the client app's resourceService.getMetrics, so both consoles plot the same
    // numbers: CPU/memory already %, network already Mbps, disk bytes/sec -> MB/s.
    const bytesToMB = (value: number) => value / (1024 * 1024);
    const points = (
      series?: MetricDataPoint[],
      convert?: (v: number) => number,
    ) =>
      (series ?? []).map((point) => ({
        timestamp: point.timestamp,
        value: convert ? convert(point.value) : point.value,
      }));

    return {
      cores: points(data.cpuUsage),
      hostCores: points(data.hostCpuUsage),
      memory: points(data.memoryUsagePercent),
      network: {
        inbound: points(data.networkInMbps),
        outbound: points(data.networkOutMbps),
      },
      disk: {
        read: points(data.diskReadBytesPerSec, bytesToMB),
        write: points(data.diskWriteBytesPerSec, bytesToMB),
      },
    };
  },

  /**
   * Get resource cost analysis
   * Returns: CostAnalysis (extracted from StandardResponse<CostAnalysis>)
   */
  async getResourceCostAnalysis(id: string): Promise<CostAnalysis> {
    const response = await apiClient.get<
      StandardResponse<CostAnalysis> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/cost-analysis`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Get resource attachments (disks and networks)
   * Returns: ResourceAttachmentsResponse (extracted from StandardResponse<ResourceAttachmentsResponse>)
   */
  async getResourceAttachments(
    id: string,
  ): Promise<ResourceAttachmentsResponse> {
    const response = await apiClient.get<
      StandardResponse<ResourceAttachmentsResponse> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/attachments`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Get paginated audit log activity for a resource.
   * Returns: { data: ResourceActivityLogEntry[]; pagination: PaginationInfo }
   */
  async getResourceActivityLog(
    id: string,
    page = 1,
    limit = 50,
  ): Promise<{ data: ResourceActivityLogEntry[]; pagination: PaginationInfo }> {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());

    const response = await apiClient.get<
      PaginatedStandardResponse<ResourceActivityLogEntry> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/activity-log?${params.toString()}`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractPaginatedData(response.data);
  },

  /**
   * Get snapshots for a VM resource.
   * Returns: ResourceSnapshot[] (extracted from StandardResponse<ResourceSnapshot[]>)
   */
  async getResourceSnapshots(id: string): Promise<ResourceSnapshot[]> {
    const response = await apiClient.get<
      StandardResponse<ResourceSnapshot[]> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/snapshots`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Get the catalog of WiService (n8n) webhooks the console may probe.
   * Returns: N8nEndpoint[] (extracted from StandardResponse<N8nEndpoint[]>)
   */
  async getN8nEndpoints(): Promise<N8nEndpoint[]> {
    const response = await apiClient.get<
      StandardResponse<N8nEndpoint[]> | ErrorResponse
    >(`/api/v1/admin/resources/n8n/endpoints`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Run one whitelisted n8n probe for a resource. The result carries the provider's raw body next
   * to WiCloud's stored state — a probe the backend could not send (no VM UUID, simulated VM) still
   * resolves successfully, with `transportError` explaining why nothing was asked.
   * Returns: N8nProbeResult (extracted from StandardResponse<N8nProbeResult>)
   */
  async getN8nProbe(
    id: string,
    endpointKey: string,
    timeframe?: string,
  ): Promise<N8nProbeResult> {
    const queryParams = new URLSearchParams();
    if (timeframe) queryParams.append("timeframe", timeframe);
    const query = queryParams.toString();

    const response = await apiClient.get<
      StandardResponse<N8nProbeResult> | ErrorResponse
    >(
      `/api/v1/admin/resources/${id}/n8n/${encodeURIComponent(endpointKey)}${query ? `?${query}` : ""}`,
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * List live VMs with the product line the pricing engine resolves for each.
   *
   * `onlyIssues` narrows it to the machines it resolves nothing for — the ones falling through to
   * the legacy component prices. `search` is routed by the term's shape exactly as the Resources
   * list routes it: a GUID matches the resource, tenant or VM uuid, an IP matches the machine's
   * address, a complete e-mail matches the owner, anything else is a substring of the VM name or
   * owner e-mail, and a phone-shaped term matches the owner's number.
   * Returns: rows plus pagination (extracted from PaginatedStandardResponse).
   */
  async getProductLineAudit(params: {
    page: number;
    pageSize: number;
    search?: string;
    tenantId?: string;
    onlyIssues?: boolean;
    status?: ResourceStatus;
    createdFrom?: string;
    createdTo?: string;
  }): Promise<{
    data: ResourceProductLineAuditRow[];
    pagination: PaginationInfo;
  }> {
    const query = new URLSearchParams();
    query.append("pageNumber", params.page.toString());
    query.append("pageSize", params.pageSize.toString());
    if (params.search) query.append("search", params.search);
    if (params.tenantId) query.append("tenantId", params.tenantId);
    if (params.onlyIssues) query.append("onlyIssues", "true");
    if (params.status) query.append("status", params.status);
    if (params.createdFrom) query.append("createdFrom", params.createdFrom);
    if (params.createdTo) query.append("createdTo", params.createdTo);

    const response = await apiClient.get<
      PaginatedStandardResponse<ResourceProductLineAuditRow> | ErrorResponse
    >(`/api/v1/admin/resources/product-line-audit?${query.toString()}`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractPaginatedData(response.data);
  },

  /**
   * Price one machine's whole stack as it stands and against every product line still on sale.
   *
   * Separate from the audit list on purpose: pricing a stack is several calls into the pricing
   * engine per product line, so the list stays cheap and this is asked for the one machine the
   * operator is about to change.
   * Returns: ResourceProductLineQuote (extracted from StandardResponse<ResourceProductLineQuote>)
   */
  async getProductLineQuote(id: string): Promise<ResourceProductLineQuote> {
    const response = await apiClient.get<
      StandardResponse<ResourceProductLineQuote> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/product-line-quote`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Move a VM to a product line that is still on sale, chosen by its id (SuperAdmin only).
   *
   * The backend reads the line by that id and stores what follows from it: the pricing key on the
   * VM, its disk and its network, and the product line and pool recorded on the VM. The machine at
   * the provider, the recorded monthly price and every existing order, billing cycle and invoice
   * are left exactly as they are — what changes is the rate a FUTURE renewal or upgrade is quoted
   * at, and the pool the next reinstall or restore uses.
   * Returns: ResourceProductLineUpdate (extracted from StandardResponse<ResourceProductLineUpdate>)
   */
  async updateProductLine(
    id: string,
    productLineId: string,
  ): Promise<ResourceProductLineUpdate> {
    const response = await apiClient.post<
      StandardResponse<ResourceProductLineUpdate> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/product-line`, { productLineId });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * The range a VM's configuration can be changed within (SuperAdmin only): the vCPU, memory and
   * disk range of its product line. Reads no provider, so it is quick and safe to repeat.
   * Returns: ResourceConfigChangeLimits
   */
  async getConfigChangeLimits(id: string): Promise<ResourceConfigChangeLimits> {
    const response = await apiClient.get<
      StandardResponse<ResourceConfigChangeLimits> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/config-change/limits`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Price a configuration change without applying anything (SuperAdmin only).
   *
   * The quote is computed against the configuration the infrastructure reports at that moment, so
   * it is only as fresh as its own `checkedAt`. Omitted axes are left unchanged.
   * Returns: ResourceConfigChangeQuote (extracted from StandardResponse<ResourceConfigChangeQuote>)
   */
  async quoteConfigChange(
    id: string,
    target: ResourceConfigChangeTarget,
  ): Promise<ResourceConfigChangeQuote> {
    const response = await apiClient.post<
      StandardResponse<ResourceConfigChangeQuote> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/config-change/quote`, {
      cores: target.cores ?? null,
      memoryMb: target.memoryMb ?? null,
      disks: target.disks ?? null,
    });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Change a VM's configuration end to end on one confirmation (SuperAdmin only).
   *
   * Applies the change to the infrastructure, brings the resource record into line with what
   * actually landed, and moves the resource and its billing cycle onto the matching price. A run
   * that changed the infrastructure and then stopped still resolves — `outcome` and `warning` say
   * what is left to do, and only a request that never reached the provider rejects.
   *
   * No payment is created: an admin-applied change moves no money through WiCloud.
   * Returns: ResourceConfigChangeExecution
   */
  async executeConfigChange(
    id: string,
    target: ResourceConfigChangeTarget,
  ): Promise<ResourceConfigChangeExecution> {
    const response = await apiClient.post<
      StandardResponse<ResourceConfigChangeExecution> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/config-change/execute`, {
      cores: target.cores ?? null,
      memoryMb: target.memoryMb ?? null,
      disks: target.disks ?? null,
    });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Delete a resource (SuperAdmin only). Asynchronous: a success response means the resource was
   * marked for deletion and the WiService termination job was queued, not that the VM is gone yet.
   * Returns: the API success message.
   */
  async deleteResource(
    id: string,
    payload?: DeleteResourcePayload,
  ): Promise<string> {
    // A VM needs the reason and the typed name; storage, networks and buckets carry neither and send
    // an empty body, since the endpoint requires one.
    const response = await apiClient.delete<MessageResponse | ErrorResponse>(
      `/api/v1/admin/resources/${id}`,
      { data: payload ?? {} },
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return response.data.message;
  },

  /**
   * Remove WiCloud's record of a VM only (SuperAdmin only): finished when the response arrives, and
   * the machine is left running at the infrastructure. Returns: the API success message.
   */
  async deleteResourceRecord(
    id: string,
    payload: DeleteResourceRecordPayload,
  ): Promise<string> {
    const response = await apiClient.delete<MessageResponse | ErrorResponse>(
      `/api/v1/admin/resources/${id}/record`,
      { data: payload },
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return response.data.message;
  },

  /**
   * Reinstall a live VM in place on its uuid, keeping its IP (SuperAdmin only).
   *
   * Omitting `templateId` keeps the VM's current OS; otherwise it is one of the admin OS image
   * catalog's ids. Wipes the VM's disks and generates a new OS login for the owner.
   * Returns: the API success message ("Reinstall started").
   */
  async reinstallResource(
    id: string,
    payload: ReinstallResourcePayload,
  ): Promise<string> {
    const response = await apiClient.post<MessageResponse | ErrorResponse>(
      `/api/v1/admin/resources/${id}/reinstall`,
      payload,
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return response.data.message;
  },

  /**
   * Restart the build of one VM that never ran — Pending on a paid order, Provisioning, or an Error
   * left by a failed provisioning (SuperAdmin only). The backend refuses every other case with the
   * reason, which is thrown as the error message.
   *
   * `recreateMissingVm` (2026-10-13) drops a recorded provider id the infrastructure no longer has,
   * so a new machine is created; a machine that still exists, or no answer, is refused.
   */
  async retryProvisioning(
    id: string,
    recreateMissingVm = false,
  ): Promise<RetryProvisioningResult> {
    const response = await apiClient.post<
      StandardResponse<RetryProvisioningResult> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/retry-provisioning`, undefined, {
      params: recreateMissingVm ? { recreateMissingVm: true } : undefined,
    });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Whether "Retry provisioning" may be offered for each of the given resources right now.
   * Batched (max 100 ids) so a list page with several failed VMs makes one call. Open to every
   * admin role — only the POST above is SuperAdmin-only. Every requested id comes back with an
   * entry; send only ids of resources whose status is Error.
   */
  async checkProvisioningRetry(
    resourceIds: string[],
  ): Promise<Record<string, ProvisioningRetryCheck>> {
    const response = await apiClient.post<
      StandardResponse<Record<string, ProvisioningRetryCheck>> | ErrorResponse
    >("/api/v1/admin/resources/retry-provisioning/check", { resourceIds });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Cut the power to several virtual machines in one request (SuperAdmin only).
   *
   * Never all-or-nothing: each machine is one `stop` posted to WiService and is refused on its own
   * terms, so a 200 carries a per-machine report rather than a single verdict. A batch where every
   * row was refused is still a 200 — read `successCount` / `failureCount`, not the HTTP status.
   * Returns: the per-machine outcome of the batch.
   */
  async bulkStopResources(
    payload: BulkStopResourcesPayload,
  ): Promise<BulkStopResourcesResult> {
    const response = await apiClient.post<
      StandardResponse<BulkStopResourcesResult> | ErrorResponse
    >("/api/v1/admin/resources/bulk-stop", payload);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * One page of the expired-VM clean-up list for a group (SuperAdmin only).
   *
   * `status` narrows any VM tab. Database only, so it answers fast; the machines'
   * state at the infrastructure comes from `getProviderState`, one VM at a time.
   */
  async getExpiredVms(
    filters: ExpiredVmFilters,
    signal?: AbortSignal,
  ): Promise<{ data: ExpiredVm[]; pagination: PaginationInfo }> {
    const params = new URLSearchParams({ group: filters.group });
    if (filters.status) params.append("status", filters.status);
    if (filters.expiredDays)
      params.append("expiredDays", filters.expiredDays.toString());
    params.append("pageNumber", filters.page.toString());
    params.append("pageSize", filters.pageSize.toString());

    const response = await apiClient.get<
      PaginatedStandardResponse<ExpiredVm> | ErrorResponse
    >(`/api/v1/admin/resources/expired?${params.toString()}`, { signal });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractPaginatedData(response.data);
  },

  /**
   * One VM's infrastructure state, read from n8n now (SuperAdmin only). Null when the id is not a live
   * VM. The page asks once per row, so each row fills in as its own answer arrives.
   */
  async getProviderState(
    id: string,
    signal?: AbortSignal,
  ): Promise<ResourceProviderState | null> {
    const params = new URLSearchParams({ ids: id });

    const response = await apiClient.get<
      StandardResponse<Record<string, ResourceProviderState>> | ErrorResponse
    >(`/api/v1/admin/resources/provider-states?${params.toString()}`, {
      signal,
    });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    const states = extractData(response.data);
    // Absent from the answer: not a live VM, so there is nothing to read.
    return states[id] ?? null;
  },

  /**
   * Delete expired VMs, or only their WiCloud records with `recordOnly` (SuperAdmin only): the two
   * are separate endpoints.
   *
   * Each VM is checked at n8n first, so the request is slow: the timeout is 15 minutes. A row that
   * was refused is still a 200 — read `successCount` / `failureCount`, not the HTTP status.
   */
  async deleteExpiredVms(
    payload: DeleteExpiredVmsPayload,
    recordOnly: boolean,
  ): Promise<DeleteExpiredVmsResult> {
    const url = recordOnly
      ? "/api/v1/admin/resources/expired/delete-record"
      : "/api/v1/admin/resources/expired/delete";
    const response = await apiClient.post<
      StandardResponse<DeleteExpiredVmsResult> | ErrorResponse
    >(url, payload, {
      timeout: 15 * 60 * 1000,
    });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /** How many rows each expired-VM tab holds, VM tabs at their default lapse (SuperAdmin only). */
  async getExpiredVmCounts(signal?: AbortSignal): Promise<ExpiredVmCounts> {
    const response = await apiClient.get<
      StandardResponse<ExpiredVmCounts> | ErrorResponse
    >("/api/v1/admin/resources/expired/counts", { signal });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /** One page (size 1–50) of disks and networks whose VM is gone or was never set (SuperAdmin only). */
  async getOrphanChildren(
    page: number,
    pageSize: number,
    filters: OrphanChildrenFilters,
    signal?: AbortSignal,
  ): Promise<{ data: OrphanChild[]; pagination: PaginationInfo }> {
    const params = new URLSearchParams({
      pageNumber: page.toString(),
      pageSize: pageSize.toString(),
      vmEndedDays: filters.vmEndedDays.toString(),
    });
    if (filters.attachment) {
      params.set("attachment", filters.attachment);
    }

    const response = await apiClient.get<
      PaginatedStandardResponse<OrphanChild> | ErrorResponse
    >(`/api/v1/admin/resources/orphan-children?${params.toString()}`, {
      signal,
    });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractPaginatedData(response.data);
  },

  /**
   * Mark orphaned disks and networks Terminated in WiCloud's records only; nothing is changed at the
   * infrastructure, which is only asked whether each former VM is gone (SuperAdmin only). A refused
   * row is still a 200 — read the counts.
   */
  async removeOrphanChildren(
    payload: RemoveOrphanChildrenPayload,
  ): Promise<RemoveOrphanChildrenResult> {
    const response = await apiClient.post<
      StandardResponse<RemoveOrphanChildrenResult> | ErrorResponse
    >("/api/v1/admin/resources/orphan-children/remove", payload);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Fill one derived resource column for the rows saved before it existed (SuperAdmin only).
   *
   * Only rows whose column is still empty are written, so calling it again changes nothing.
   * Returns: how many rows the run looked at and how many it filled.
   */
  async backfillResourceField(
    field: ResourceBackfillField,
  ): Promise<ResourceFieldBackfillResult> {
    const response = await apiClient.post<
      StandardResponse<ResourceFieldBackfillResult> | ErrorResponse
    >(`/api/v1/admin/resources/backfill/${field}`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Change a resource's expiry date (SuperAdmin only).
   *
   * Writes ExpiresAt only — no billing cycle is extended and nothing is charged. The expiry scan
   * jobs pick the new date up on their next run, so a past date suspends the VM later, not now.
   * Returns: the stored expiry date as an ISO UTC string.
   */
  async updateResourceExpiry(
    id: string,
    expiresAt: string,
    reason?: string,
  ): Promise<string> {
    const response = await apiClient.put<
      StandardResponse<string> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/expiry`, { expiresAt, reason });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Write a new IP address into a VM's cloud-init network configuration, at the provider
   * (SuperAdmin only).
   *
   * The counterpart of `updateResourcePublicIp`: that one corrects WiCloud's record of an address
   * the machine already has, this one changes the machine. Cloud-init is the only endpoint that
   * can — the network endpoint reaches VLAN, switch, link state and firewall and no address.
   *
   * **The new address is not live until the VM is restarted**, and nothing here restarts it. Once
   * the provider accepts, WiCloud's own record is brought into line across the VM's UUID.
   */
  async applyVmIpAddress(
    id: string,
    payload: ApplyVmIpAddressPayload,
  ): Promise<ApplyVmIpAddressResult> {
    const response = await apiClient.post<
      StandardResponse<ApplyVmIpAddressResult> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/apply-ip-address`, payload);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Correct the public IP address WiCloud has on record for a VM (SuperAdmin only).
   *
   * Writes WiCloud's own record only — the provider is never contacted, so nothing is reassigned
   * on the hypervisor, no interface is reconfigured and no DNS record is rewritten. The correction
   * covers every place an address lives behind that VM's UUID: the searchable column, the VM's
   * metadata, its primary interface, and the network rows it solely owns. Returns the rows and
   * fields it actually wrote.
   *
   * The address may carry a CIDR suffix; it is kept in the metadata documents and stripped for the
   * searchable column.
   */
  async updateResourcePublicIp(
    id: string,
    ipAddress: string,
    reason?: string,
  ): Promise<ResourcePublicIpUpdate> {
    const response = await apiClient.put<
      StandardResponse<ResourcePublicIpUpdate> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/public-ip`, { ipAddress, reason });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Correct the status WiCloud has on record for a resource (SuperAdmin only).
   *
   * Writes Status only — the provider is never contacted, so this neither starts, stops nor
   * suspends anything. The change cascades to the disk and network rows the resource solely owns,
   * which carry the same VM UUID. Returns the rows that actually moved.
   *
   * Any ResourceStatus is accepted; the endpoint does not restrict the operator's choice.
   */
  async updateResourceStatus(
    id: string,
    status: ResourceStatus,
  ): Promise<ResourceStatusUpdate> {
    const response = await apiClient.put<
      StandardResponse<ResourceStatusUpdate> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/status`, { status });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Run one VM power action (SuperAdmin only).
   *
   * The action name is also the route segment, so all seven endpoints share this one call. A
   * success means the provider accepted the action, not that the guest OS has finished acting on
   * it — shutdown and reboot hand the request to the guest, so the status catches up on the next
   * sync rather than in this response.
   * Returns: the API success message.
   */
  /**
   * Check whether the VM this resource is recorded against is actually running on the
   * infrastructure (SuperAdmin only). Read-only — nothing is written on either side.
   *
   * `vmUuid` names the machine to check instead of the recorded one — for a resource with none, or
   * to re-link a VM to a different machine. It must exist on the infrastructure, not be locked and
   * not be recorded against another resource.
   * Returns: UpdateByUuidPreview
   */
  async previewUpdateByUuid(
    id: string,
    vmUuid?: string,
  ): Promise<UpdateByUuidPreview> {
    const response = await apiClient.post<
      StandardResponse<UpdateByUuidPreview> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/update-by-uuid/preview`, undefined, {
      params: vmUuid ? { vmUuid } : undefined,
    });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Recover a VM from the infrastructure end to end (SuperAdmin only).
   *
   * Rebuilds the record from the running machine, brings the resource back into service and closes
   * the order that bought it. A run that committed something and then stopped still resolves —
   * `outcome` and `warning` say what is left to do.
   *
   * Sends no email, moves no money and changes no dates. A payment is only ever read, never
   * settled. Nothing on the infrastructure is changed or deleted — this adopts the machine the
   * resource is recorded against, or the one named by `vmUuid` (re-linking leaves the previous
   * machine running, untracked). `poolName` and `osTemplateId` set the machine's pool and OS, which
   * the infrastructure does not report; omitted, the recorded ones are kept.
   * Returns: UpdateByUuidExecution
   */
  async updateResourceByUuid(
    id: string,
    vmUuid?: string,
    poolName?: string,
    osTemplateId?: string,
  ): Promise<UpdateByUuidExecution> {
    const response = await apiClient.post<
      StandardResponse<UpdateByUuidExecution> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/update-by-uuid`, undefined, {
      params: { vmUuid, poolName, osTemplateId },
    });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Set a new OS password on a VM (SuperAdmin only).
   *
   * Pass `password` to use a value the operator chose; omit it and the infrastructure generates
   * one. The password comes back once, in this response. What happens next depends on the guest OS:
   * Windows picks it up without restarting, Linux is rebooted by the provider itself. `requiresReboot`
   * is hardcoded true and does not express either, so the panel branches on the OS instead.
   */
  async resetVmPassword(
    id: string,
    password?: string,
  ): Promise<ResetVmPasswordResult> {
    const response = await apiClient.post<
      StandardResponse<ResetVmPasswordResult> | ErrorResponse
    >(
      `/api/v1/admin/resources/${id}/reset-password`,
      // Truthy, not `!== undefined`: absent, null and empty all mean "let the infrastructure
      // generate one" — the provider's own reading of an empty `password`, which WiCloud passes
      // through rather than giving empty a third meaning. Omitting the body is the tidiest way to
      // say it.
      password ? { password } : undefined,
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Mint a noVNC console link for any tenant's VM (SuperAdmin only). Every call is audited on the
   * backend, so only call it when the operator has asked to open the console.
   */
  async getConsole(id: string): Promise<ResourceConsole> {
    const response = await apiClient.get<
      StandardResponse<ResourceConsole> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/console`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Change a VM's power state by calling the WiService (n8n) webhook directly (SuperAdmin only).
   *
   * The break-glass twin of `runPowerAction`: it does not move the resource's own status or write a
   * resource-action row. Nothing is left stale — the status sync re-reads power state by itself.
   */
  async runN8nPowerAction(
    resourceId: string,
    action: N8nPowerAction,
  ): Promise<N8nVmActionResult> {
    const response = await apiClient.post<
      StandardResponse<N8nVmActionResult> | ErrorResponse
    >(`/api/v1/admin/resources/${resourceId}/n8n/actions/power`, { action });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /** Delete the recorded VM directly through n8n. WiCloud's record and billing stay unchanged. */
  async runN8nDeleteAction(
    resourceId: string,
    vmUuid: string,
  ): Promise<N8nVmActionResult> {
    const response = await apiClient.post<
      StandardResponse<N8nVmActionResult> | ErrorResponse
    >(`/api/v1/admin/resources/${resourceId}/n8n/actions/delete`, { vmUuid });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Set a VM's vCPU and/or memory through the WiService (n8n) webhook (SuperAdmin only).
   *
   * Both figures are absolute totals, not deltas; omit one to leave that half alone. The webhook
   * only runs on a stopped VM, and WiCloud's stored configuration is NOT updated — reconcile it
   * from Compare configuration afterwards.
   */
  async runN8nSpecsAction(
    resourceId: string,
    payload: { cores?: number; memoryMb?: number },
  ): Promise<N8nVmActionResult> {
    const response = await apiClient.post<
      StandardResponse<N8nVmActionResult> | ErrorResponse
    >(`/api/v1/admin/resources/${resourceId}/n8n/actions/specs`, payload);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Grow one of a VM's disks through the WiService (n8n) webhook (SuperAdmin only).
   *
   * `growByGb` is capacity to ADD, not the size to end up at — a virtual disk grows and never
   * shrinks, so this cannot be undone. WiCloud's stored configuration is NOT updated.
   */
  async runN8nDiskAction(
    resourceId: string,
    payload: { diskName: string; growByGb: number },
  ): Promise<N8nVmActionResult> {
    const response = await apiClient.post<
      StandardResponse<N8nVmActionResult> | ErrorResponse
    >(`/api/v1/admin/resources/${resourceId}/n8n/actions/disk`, payload);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Transfers a VM's ownership to another customer: the resource row itself changes hands, with its
   * disks, networks, snapshots, firewall rules and stored login.
   *
   * Nothing reaches the hypervisor — the machine keeps running, keeps its IP and keeps its
   * password, which is exactly why the caller must reset that password afterwards. The paid-through
   * date is carried over at zero cost; the previous owner keeps their orders, payments and
   * invoices.
   */
  async transferResourceOwnership(
    resourceId: string,
    payload: TransferResourceOwnershipPayload,
  ): Promise<TransferResourceOwnershipResult> {
    const response = await apiClient.post<
      StandardResponse<TransferResourceOwnershipResult> | ErrorResponse
    >(`/api/v1/admin/resources/${resourceId}/transfer-ownership`, payload);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  async runPowerAction(
    id: string,
    action: ResourcePowerAction,
    reason?: string,
  ): Promise<string> {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : "";

    const response = await apiClient.post<MessageResponse | ErrorResponse>(
      `/api/v1/admin/resources/${id}/${action}${params}`,
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return response.data.message;
  },
  /**
   * Restores a deleted VM inside its window (SuperAdmin), on its uuid and current OS: the API queues
   * the rebuild and the VM is back in the fleet reading Decommissioning until it runs. Refuses (409) a
   * row whose window has closed or whose name another resource has taken since.
   */
  async restore(id: string): Promise<string> {
    const response = await apiClient.post<MessageResponse | ErrorResponse>(
      `/api/v1/admin/resources/${id}/restore`,
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return response.data.message;
  },

  /**
   * Starts the finalize of a deleted VM now (SuperAdmin): the API enqueues the same job the schedule
   * runs one hour after the deletion, for a row whose scheduled one never ran or failed for good. It
   * refuses (409) a row that is not a deleted VM, or one still inside the window it is kept for.
   */
  async finalizeDeletion(id: string): Promise<string> {
    const response = await apiClient.post<MessageResponse | ErrorResponse>(
      `/api/v1/admin/resources/${id}/finalize-deletion`,
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return response.data.message;
  },

  /**
   * Sends this machine's owner an expiry reminder covering this machine alone.
   *
   * Whether the machine qualifies is the API's call — type, status, term and window are all
   * checked there, and each refusal comes back as a sentence naming what is in the way.
   */
  async sendExpiryReminder(
    resourceId: string,
  ): Promise<SendResourceExpiryReminderResultDto> {
    const response = await apiClient.post<
      StandardResponse<SendResourceExpiryReminderResultDto>
    >(`/api/v1/admin/resources/${resourceId}/send-expiry-reminder`);
    return extractData(response.data);
  },

  /**
   * Read a VM's firewall rules and options straight from the hypervisor (SuperAdmin, SupportAdmin).
   *
   * Not called on page load — every read reaches WiService (n8n) — the firewall panel asks only
   * while the Networking tab is open.
   * Returns: FirewallRulesResponse (extracted from StandardResponse<FirewallRulesResponse>)
   */
  async getFirewallRules(id: string): Promise<FirewallRulesResponse> {
    const response = await apiClient.get<
      StandardResponse<FirewallRulesResponse> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/firewall/rules`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Read a VM's recent firewall log lines (SuperAdmin, SupportAdmin). Called only when the Logs
   * sub-tab is opened.
   * Returns: FirewallLog[] (extracted from StandardResponse<FirewallLog[]>)
   */
  async getFirewallLogs(id: string): Promise<FirewallLog[]> {
    const response = await apiClient.get<
      StandardResponse<FirewallLog[]> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/firewall/logs`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * List the firewall macros the provider recognises (SuperAdmin, SupportAdmin). Not resource-scoped
   * — the catalog is the same for every VM.
   * Returns: FirewallMacro[] (extracted from StandardResponse<FirewallMacro[]>)
   */
  async getFirewallMacros(): Promise<FirewallMacro[]> {
    const response = await apiClient.get<
      StandardResponse<FirewallMacro[]> | ErrorResponse
    >(`/api/v1/admin/resources/firewall/macros`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Add a firewall rule to a VM (SuperAdmin only).
   * Returns: N8nVmActionResult
   */
  async addFirewallRule(
    id: string,
    rule: CreateFirewallRuleRequest,
  ): Promise<N8nVmActionResult> {
    const response = await apiClient.post<
      StandardResponse<N8nVmActionResult> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/firewall/rules`, rule);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Edit one firewall rule by position (SuperAdmin only). Only the fields `buildRuleUpdate` decided
   * changed travel — WiService keeps every field the body leaves out.
   * Returns: N8nVmActionResult
   */
  async updateFirewallRule(
    id: string,
    pos: number,
    update: CreateFirewallRuleRequest,
  ): Promise<N8nVmActionResult> {
    const response = await apiClient.patch<
      StandardResponse<N8nVmActionResult> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/firewall/rules/${pos}`, update);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Delete one firewall rule by position (SuperAdmin only).
   * Returns: N8nVmActionResult
   */
  async deleteFirewallRule(
    id: string,
    pos: number,
  ): Promise<N8nVmActionResult> {
    const response = await apiClient.delete<
      StandardResponse<N8nVmActionResult> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/firewall/rules/${pos}`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Replace a VM's firewall options (SuperAdmin only). Every field is required — the endpoint can no
   * longer tell "not set" from "explicitly cleared" — so the caller coalesces with the values
   * currently shown before sending a partial change (see `ResourceFirewallPanel.applyOptionsChange`).
   * Returns: N8nVmActionResult
   */
  async updateFirewallOptions(
    id: string,
    options: FirewallOptions,
  ): Promise<N8nVmActionResult> {
    const response = await apiClient.put<
      StandardResponse<N8nVmActionResult> | ErrorResponse
    >(`/api/v1/admin/resources/${id}/firewall/options`, options);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },
};

/** What a manual per-machine expiry-reminder send did. */
export interface SendResourceExpiryReminderResultDto {
  resourceName: string;
  /** Whole days left on the customer's calendar. */
  daysUntilExpiry: number;
  /** Where the email went, or null when only the in-app reminder was written. */
  recipientEmail: string | null;
  /** Whether an email was queued. */
  emailSent: boolean;
  /** Whether an in-app notification was written. */
  inAppSent: boolean;
  /** A sentence written by the API, shown to the operator as-is. */
  message: string;
}
